/**
 * /admin/pipeline — Kanban board.
 *
 * Columns are the 8 lead statuses. Cards are draggable (HTML5 DnD) between
 * columns; dropping onto a new column PATCHes the lead status via the
 * existing /leads/{id} endpoint. Operator scope is enforced server-side.
 *
 * Each card shows: lead name, phone last-4, interest, verification badge,
 * assigned staff email, days-since-created.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Phone,
  RefreshCw,
  Loader2,
  ShieldCheck,
  ShieldOff,
  XCircle,
  Inbox,
  Hourglass,
  Award,
  Archive,
  CheckCircle2,
  IndianRupee,
  User,
  Layers,
} from "lucide-react";
import { adminApi, CRM, tokenStorage, currentUser } from "@/lib/api";
import Logo from "@/components/Logo";

const STAGE_META = {
  new:       { label: "New",       tint: "border-blue-400/30 bg-blue-500/[0.06]",  icon: Inbox },
  contacted: { label: "Contacted", tint: "border-yellow-300/30 bg-yellow-500/[0.06]", icon: Hourglass },
  qualified: { label: "Qualified", tint: "border-emerald-400/30 bg-emerald-500/[0.06]", icon: Award },
  pending:   { label: "Pending",   tint: "border-amber-400/30 bg-amber-500/[0.06]",  icon: Hourglass },
  approved:  { label: "Approved",  tint: "border-emerald-500/30 bg-emerald-500/[0.08]", icon: CheckCircle2 },
  disbursed: { label: "Disbursed", tint: "border-[#D4AF37]/40 bg-[#D4AF37]/[0.08]",  icon: IndianRupee },
  rejected:  { label: "Rejected",  tint: "border-red-500/30 bg-red-500/[0.06]",     icon: XCircle },
  closed:    { label: "Closed",    tint: "border-zinc-400/30 bg-zinc-500/[0.06]",   icon: Archive },
};

const VERIF_META = {
  pending:  { color: "text-amber-300",   Icon: Hourglass },
  verified: { color: "text-emerald-300", Icon: ShieldCheck },
  rejected: { color: "text-red-300",     Icon: XCircle },
};

export default function KanbanPipeline() {
  const nav = useNavigate();
  const me = currentUser();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(null); // {leadId, from}
  const [savingId, setSavingId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const d = await CRM.pipeline({ limit_per_column: 60 });
      setBoard(d);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin", { replace: true });
      setError(e?.response?.data?.detail || "Failed to load pipeline.");
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => {
    if (!tokenStorage.get()) { nav("/admin", { replace: true }); return; }
    refresh();
  }, [nav, refresh]);

  const canWrite = me && ["admin", "manager", "receptionist", "operator"].includes(me.role);

  async function moveCard(leadId, from, to) {
    if (!canWrite || from === to) return;
    setSavingId(leadId);
    // Optimistic UI: move card first, revert on error.
    const optimistic = structuredClone(board);
    const src = optimistic.columns[from];
    const dest = optimistic.columns[to];
    if (!src || !dest) return;
    const idx = src.items.findIndex((c) => c.id === leadId);
    if (idx === -1) return;
    const [card] = src.items.splice(idx, 1);
    src.total -= 1;
    card.status = to;
    dest.items.unshift(card);
    dest.total += 1;
    setBoard(optimistic);
    try {
      await adminApi.patch(`/leads/${leadId}`, { status: to });
    } catch (e) {
      // Revert
      setError(e?.response?.data?.detail || "Move failed — reverted.");
      refresh();
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div data-testid="kanban-page" className="min-h-screen bg-[#04102B] text-white">
      <header className="border-b border-white/10 backdrop-blur-lg bg-black/20 sticky top-0 z-30">
        <div className="max-w-full mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <button
            data-testid="kanban-back"
            onClick={() => nav("/admin/leads")}
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F0D788]"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex items-center gap-3">
            <Logo size={40} testId="kanban-logo" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Kanban Pipeline</div>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/dashboard" data-testid="kanban-goto-dashboard" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 text-white/85 text-sm hover:bg-white/[0.09]">
              <Layers size={13} /> Dashboard
            </Link>
            <button
              data-testid="kanban-refresh"
              onClick={refresh}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-xs text-white/85 hover:bg-white/[0.06]"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-full px-4 md:px-8 py-6">
        <div className="mb-4 flex items-baseline justify-between">
          <div>
            <h1 className="font-display text-2xl md:text-3xl">Deal pipeline</h1>
            <p className="mt-1 text-white/60 text-sm max-w-xl">
              Drag any card between columns to update the stage. Server enforces role checks —
              operators only see their assigned leads.
            </p>
          </div>
          {savingId && <span className="text-xs text-[#F0D788] inline-flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Saving…</span>}
        </div>

        {error && <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-200">{error}</div>}
        {loading && <div className="py-24 text-center text-white/60">Loading board…</div>}

        {!loading && board && (
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-columns">
            {board.order.map((status) => {
              const col = board.columns[status];
              const meta = STAGE_META[status];
              const HdrIcon = meta?.icon || Inbox;
              return (
                <div
                  key={status}
                  data-testid={`kanban-col-${status}`}
                  onDragOver={(e) => { if (dragging) e.preventDefault(); }}
                  onDrop={() => {
                    if (dragging && dragging.from !== status) moveCard(dragging.leadId, dragging.from, status);
                    setDragging(null);
                  }}
                  className={`w-64 flex-shrink-0 rounded-2xl border ${meta.tint} transition-all ${
                    dragging && dragging.from !== status ? "ring-2 ring-[#D4AF37]/40" : ""
                  }`}
                >
                  <div className="px-4 py-3 flex items-center gap-2 border-b border-white/10">
                    <HdrIcon size={14} className="text-white/85" />
                    <div className="text-sm font-semibold text-white">{meta.label}</div>
                    <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-black/25 border border-white/10 text-white/75 font-mono">
                      {col?.total ?? 0}
                    </span>
                  </div>
                  <div className="p-2 space-y-2 max-h-[calc(100vh-260px)] overflow-y-auto">
                    {(col?.items || []).length === 0 && (
                      <div className="py-6 text-center text-[11px] text-white/40">No leads in this stage.</div>
                    )}
                    {(col?.items || []).map((card) => (
                      <KanbanCard
                        key={card.id}
                        card={card}
                        status={status}
                        draggable={canWrite}
                        onDragStart={() => setDragging({ leadId: card.id, from: status })}
                        onDragEnd={() => setDragging(null)}
                      />
                    ))}
                    {col?.total > (col?.items?.length || 0) && (
                      <div className="text-center text-[10px] text-white/50 py-2">
                        +{col.total - col.items.length} more…
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function KanbanCard({ card, status, draggable, onDragStart, onDragEnd }) {
  const v = card.verification_status || "pending";
  const vm = VERIF_META[v] || VERIF_META.pending;
  const daysAgo = daysSince(card.created_at);
  return (
    <div
      data-testid={`kanban-card-${card.id}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`rounded-xl bg-[#0A1F44] border border-white/10 hover:border-[#D4AF37]/40 p-3 ${
        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-default"
      } transition-colors`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-white text-[13px] font-medium leading-snug break-words">{card.name}</div>
        <span className={`inline-flex items-center gap-1 text-[10px] ${vm.color} shrink-0`}>
          <vm.Icon size={9} />
        </span>
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-white/55">
        <Phone size={9} /> ****{(card.phone || "").slice(-4)}
      </div>
      <div className="mt-2 text-[11px] text-white/70 line-clamp-1">{card.interest || "—"}</div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-[10px] text-white/45">
          {daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : `${daysAgo}d ago`}
        </div>
        {card.assigned_to_email && (
          <div className="text-[10px] text-[#F0D788] inline-flex items-center gap-1 max-w-[120px] truncate" title={card.assigned_to_email}>
            <User size={9} /> {card.assigned_to_email.split("@")[0]}
          </div>
        )}
      </div>
    </div>
  );
}

function daysSince(iso) {
  try {
    const d = new Date(iso);
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  } catch { return 0; }
}
